/**
 * Utils to calculate the credit renewal date based on subscription details.
 *
 * Rules implemented:
 * - If the plan is monthly (billingIntervalDays <= 31), return subscriptionEndDate as-is.
 * - If the plan is yearly:
 *   - Take only the DAY from subscriptionEndDate (e.g. 10).
 *   - If today's day < target day → return that day in the current month.
 *   - If today's day >= target day → return that day in the next month.
 *   - If the target day doesn't exist in the month, clamp to the last day of that month.
 */

export type SubscriptionLike = {
    subscriptionEndDate?: string | null;
    billingIntervalDays?: number | null;
} | null;

export function getCreditRenewalDate(subscription: SubscriptionLike): Date | null {
    if (!subscription?.subscriptionEndDate) return null;

    const MONTHLY_BILLING_THRESHOLD = 31;

    const endDate = new Date(subscription.subscriptionEndDate);

    // Monthly plan: return the subscription end date directly
    if (
        subscription?.billingIntervalDays !== null &&
        subscription?.billingIntervalDays !== undefined &&
        subscription.billingIntervalDays <= MONTHLY_BILLING_THRESHOLD
    ) {
        return endDate;
    }

    // Yearly plan: determine based on current day vs target day
    const today = new Date();
    const targetDay = endDate.getDate();

    let targetMonthIndex: number;
    let targetYear: number = today.getFullYear();

    if (today.getDate() < targetDay) {
        // current month
        targetMonthIndex = today.getMonth();
    } else {
        // next month
        targetMonthIndex = today.getMonth() + 1;
        if (targetMonthIndex > 11) {
            targetMonthIndex = 0;
            targetYear += 1;
        }
    }

    // number of days in the target month
    const daysInTargetMonth = new Date(targetYear, targetMonthIndex + 1, 0).getDate();

    // clamp to avoid overflow (e.g., day 31 in April)
    const safeDay = Math.min(targetDay, daysInTargetMonth);

    return new Date(targetYear, targetMonthIndex, safeDay);
}
